import React, { useCallback, useEffect, useRef, useState } from "react";
import {
  Plus, Trash2, Loader2, Sparkles, Copy, FileDown, Send,
  Bold, Italic, List, ListOrdered, Heading2, Heading3, Check,
  LayoutTemplate, Building2, Mic2, PartyPopper, Handshake, Mail,
} from "lucide-react";
import { toast } from "sonner";

import PageHeader from "@/components/PageHeader";
import {
  Dialog, DialogContent, DialogDescription, DialogFooter,
  DialogHeader, DialogTitle,
} from "@/components/ui/dialog";
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem,
  DropdownMenuLabel, DropdownMenuSeparator, DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { api } from "@/lib/api";
import { appConfirm } from "@/components/AppDialogHost";

const TEMPLATES = [
  { key: "about_us", label: "About Us", desc: "Website page — your story & mission", icon: Building2 },
  { key: "show_description", label: "Show Description", desc: "Punchy blurb for a show", icon: Mic2 },
  { key: "event_promo", label: "Event Promo", desc: "Hype copy for a live event", icon: PartyPopper },
  { key: "sponsor_pitch", label: "Sponsor Pitch", desc: "Win-the-advertiser letter", icon: Handshake },
];

function wordCount(html) {
  const text = (html || "").replace(/<[^>]+>/g, " ");
  return text.split(/\s+/).filter(Boolean).length;
}

export default function WritersDesk() {
  const [docs, setDocs] = useState([]);
  const [activeId, setActiveId] = useState(null);
  const [title, setTitle] = useState("");
  const [words, setWords] = useState(0);
  const [saving, setSaving] = useState(false);
  const [savedAt, setSavedAt] = useState(null);
  const [instruction, setInstruction] = useState("");
  const [targetWords, setTargetWords] = useState(200);
  const [aiBusy, setAiBusy] = useState(null); // "generate" | "longer" | "rewrite"
  const [sending, setSending] = useState(false);
  const [templateBusy, setTemplateBusy] = useState(null);
  const [emailOpen, setEmailOpen] = useState(false);
  const [emailTo, setEmailTo] = useState("");
  const [emailSubject, setEmailSubject] = useState("");
  const [emailNote, setEmailNote] = useState("");
  const [emailSending, setEmailSending] = useState(false);

  const editorRef = useRef(null);
  const contentRef = useRef("");
  const saveTimer = useRef(null);
  const titleRef = useRef("");
  titleRef.current = title;

  const loadDocs = useCallback(async () => {
    try {
      const r = await api.get("/writer/docs");
      setDocs(r.data.items || []);
      return r.data.items || [];
    } catch { toast.error("Failed to load documents"); return []; }
  }, []);

  const openDoc = useCallback(async (docId) => {
    try {
      const r = await api.get(`/writer/docs/${docId}`);
      setActiveId(docId);
      setTitle(r.data.title || "");
      contentRef.current = r.data.content_html || "";
      if (editorRef.current) editorRef.current.innerHTML = contentRef.current;
      setWords(wordCount(contentRef.current));
      setSavedAt(r.data.updated_at);
    } catch { toast.error("Failed to open document"); }
  }, []);

  const newDoc = useCallback(async () => {
    try {
      const r = await api.post("/writer/docs", { title: "", content_html: "" });
      await loadDocs();
      await openDoc(r.data.doc_id);
    } catch { toast.error("Failed to create document"); }
  }, [loadDocs, openDoc]);

  useEffect(() => {
    (async () => {
      const items = await loadDocs();
      const wanted = new URLSearchParams(window.location.search).get("doc");
      if (wanted && items.some((d) => d.doc_id === wanted)) await openDoc(wanted);
      else if (items.length > 0) await openDoc(items[0].doc_id);
      else await newDoc();
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const saveNow = useCallback(async (docId) => {
    if (!docId) return;
    setSaving(true);
    try {
      await api.put(`/writer/docs/${docId}`, {
        title: titleRef.current,
        content_html: contentRef.current,
      });
      setSavedAt(new Date().toISOString());
      setDocs((ds) => ds.map((d) => d.doc_id === docId
        ? { ...d, title: titleRef.current || "Untitled document", word_count: wordCount(contentRef.current) }
        : d));
    } catch { /* autosave — retried on next input */ }
    finally { setSaving(false); }
  }, []);

  const scheduleSave = useCallback(() => {
    clearTimeout(saveTimer.current);
    saveTimer.current = setTimeout(() => saveNow(activeId), 1200);
  }, [activeId, saveNow]);

  const onEditorInput = () => {
    contentRef.current = editorRef.current?.innerHTML || "";
    setWords(wordCount(contentRef.current));
    scheduleSave();
  };

  const deleteDoc = async (docId) => {
    if (!await appConfirm("Delete this document?")) return;
    try {
      await api.delete(`/writer/docs/${docId}`);
      const items = await loadDocs();
      if (docId === activeId) {
        if (items.length > 0) await openDoc(items[0].doc_id);
        else await newDoc();
      }
      toast.success("Deleted");
    } catch { toast.error("Delete failed"); }
  };

  const exec = (cmd, val = null) => {
    editorRef.current?.focus();
    document.execCommand(cmd, false, val);
    onEditorInput();
  };

  const runAi = async (mode) => {
    setAiBusy(mode);
    try {
      const r = await api.post("/writer/ai", {
        mode,
        instruction,
        current_html: contentRef.current,
        target_words: Number(targetWords) || 0,
      });
      contentRef.current = r.data.content_html;
      if (editorRef.current) editorRef.current.innerHTML = contentRef.current;
      setWords(r.data.word_count || wordCount(contentRef.current));
      if (!titleRef.current && instruction) setTitle(instruction.slice(0, 80));
      scheduleSave();
      toast.success(mode === "generate" ? "Draft written" : mode === "longer" ? "Expanded" : "Rewritten");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "AI request failed", { duration: 10000 });
    } finally { setAiBusy(null); }
  };

  const runTemplate = async (key, label) => {
    setTemplateBusy(key);
    const t = toast.loading(`Writing your ${label} draft…`);
    try {
      const r = await api.post("/writer/template", { template_key: key, details: instruction });
      await loadDocs();
      await openDoc(r.data.doc_id);
      toast.success(`${label} draft ready — tweak away`, { id: t });
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Template draft failed", { id: t, duration: 8000 });
    } finally { setTemplateBusy(null); }
  };

  const copyText = () => {
    const text = (editorRef.current?.innerText || "").trim();
    navigator.clipboard.writeText(text);
    toast.success("Text copied");
  };
  const copyHtml = () => {
    navigator.clipboard.writeText(contentRef.current);
    toast.success("HTML copied — paste into your website editor");
  };
  const downloadTxt = () => {
    const text = (editorRef.current?.innerText || "").trim();
    const blob = new Blob([`${title}\n\n${text}`], { type: "text/plain;charset=utf-8" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `${(title || "document").replace(/[^\w\s-]/g, "").trim() || "document"}.txt`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const openEmailDialog = () => {
    if (!contentRef.current.trim()) { toast.error("Nothing to send yet"); return; }
    setEmailSubject(title || "A message from our station");
    setEmailOpen(true);
  };

  const sendDocEmail = async () => {
    setEmailSending(true);
    try {
      const r = await api.post(`/writer/docs/${activeId}/email`, {
        to_email: emailTo,
        subject: emailSubject,
        note: emailNote,
      });
      toast.success(`Emailed to ${r.data.to_email} — replies come straight to your inbox`, { duration: 8000 });
      setEmailOpen(false);
      setEmailTo(""); setEmailNote("");
    } catch (e) {
      const d = e?.response?.data?.detail;
      toast.error(typeof d === "string" ? d : "Send failed", { duration: 12000 });
    } finally { setEmailSending(false); }
  };

  const sendToBlog = async () => {
    if (!contentRef.current.trim()) { toast.error("Nothing to send yet"); return; }
    setSending(true);
    try {
      const r = await api.post("/blog-connector/wordpress/publish", {
        headline: title || "Untitled",
        content_html: contentRef.current,
        status: "draft",
      });
      toast.success(`Sent to WordPress as a draft${r.data?.link ? ` — ${r.data.link}` : ""}`, { duration: 8000 });
    } catch (e) {
      const d = e?.response?.data?.detail;
      toast.error(typeof d === "string" ? d : "Send failed — connect WordPress in Blog Connector first", { duration: 8000 });
    } finally { setSending(false); }
  };

  const TOOLBAR = [
    { icon: Heading2, cmd: () => exec("formatBlock", "h2"), label: "Heading", tid: "wd-h2" },
    { icon: Heading3, cmd: () => exec("formatBlock", "h3"), label: "Subheading", tid: "wd-h3" },
    { icon: Bold, cmd: () => exec("bold"), label: "Bold", tid: "wd-bold" },
    { icon: Italic, cmd: () => exec("italic"), label: "Italic", tid: "wd-italic" },
    { icon: List, cmd: () => exec("insertUnorderedList"), label: "Bullets", tid: "wd-ul" },
    { icon: ListOrdered, cmd: () => exec("insertOrderedList"), label: "Numbered", tid: "wd-ol" },
  ];

  return (
    <div className="max-w-6xl" data-testid="writers-desk-page">
      <PageHeader
        eyebrow="Editorial Desk"
        title="Just write."
        description="A simple space for website copy, show descriptions and blurbs — with an AI assistant that knows your station. No pipelines, no publishing pressure."
      />

      <div className="grid lg:grid-cols-[240px_1fr] gap-5">
        {/* ── Documents list ─────────────────────────────── */}
        <div>
          <button onClick={newDoc} data-testid="wd-new-doc-btn"
            className="w-full inline-flex items-center justify-center gap-1.5 text-xs font-bold px-3 py-2.5 rounded-lg bg-brand-amber text-black hover:bg-brand-amber/90 transition mb-2">
            <Plus className="h-3.5 w-3.5" /> New document
          </button>
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <button data-testid="wd-templates-btn" disabled={!!templateBusy}
                className="w-full inline-flex items-center justify-center gap-1.5 text-xs font-semibold px-3 py-2.5 rounded-lg border border-brand-amber/40 text-brand-amber hover:bg-brand-amber/10 transition mb-3 disabled:opacity-60">
                {templateBusy ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <LayoutTemplate className="h-3.5 w-3.5" />}
                {templateBusy ? "Writing draft…" : "Templates"}
              </button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="start" className="w-64">
              <DropdownMenuLabel className="font-mono text-[10px] uppercase tracking-[0.2em] text-ink-muted">
                AI writes a first draft for your station
              </DropdownMenuLabel>
              <DropdownMenuSeparator />
              {TEMPLATES.map((t) => (
                <DropdownMenuItem key={t.key} data-testid={`wd-template-${t.key}`}
                  disabled={!!templateBusy}
                  onClick={() => runTemplate(t.key, t.label)}
                  className="cursor-pointer gap-2.5 py-2">
                  <t.icon className="h-4 w-4 text-brand-amber shrink-0" />
                  <span className="flex flex-col">
                    <span className="text-[12.5px] font-semibold">{t.label}</span>
                    <span className="text-[10.5px] text-ink-muted">{t.desc}</span>
                  </span>
                </DropdownMenuItem>
              ))}
              <DropdownMenuSeparator />
              <div className="px-2 py-1.5 text-[10px] text-ink-muted leading-snug">
                Tip: type specifics (event date, sponsor name…) in the AI box first — they'll be woven in.
              </div>
            </DropdownMenuContent>
          </DropdownMenu>
          <div className="flex flex-col gap-1" data-testid="wd-doc-list">
            {docs.map((d) => (
              <div key={d.doc_id}
                className={`group flex items-center gap-2 rounded-lg border px-3 py-2 cursor-pointer transition ${
                  d.doc_id === activeId ? "border-brand-amber/50 bg-brand-amber/5" : "border-line bg-bg-surface hover:border-brand-amber/30"}`}
                onClick={() => openDoc(d.doc_id)}
                data-testid={`wd-doc-${d.doc_id}`}>
                <div className="flex-1 min-w-0">
                  <div className="text-[12px] font-semibold truncate">{d.title || "Untitled document"}</div>
                  <div className="text-[10px] text-ink-muted">{d.word_count || 0} words</div>
                </div>
                <button onClick={(e) => { e.stopPropagation(); deleteDoc(d.doc_id); }}
                  data-testid={`wd-delete-${d.doc_id}`}
                  className="opacity-0 group-hover:opacity-100 text-ink-muted hover:text-red-400 transition">
                  <Trash2 className="h-3.5 w-3.5" />
                </button>
              </div>
            ))}
          </div>
        </div>

        {/* ── Editor ─────────────────────────────────────── */}
        <div className="rounded-xl border border-line bg-bg-surface p-5">
          {/* AI assistant bar */}
          <div className="rounded-lg border border-line bg-bg-elevated p-3 mb-4">
            <div className="font-mono text-[10px] uppercase tracking-[0.2em] text-ink-muted mb-2">// AI assistant — knows your station &amp; episodes</div>
            <div className="flex flex-wrap gap-2 items-center">
              <input value={instruction} onChange={(e) => setInstruction(e.target.value)}
                data-testid="wd-ai-instruction"
                placeholder='e.g. Write a longer description of "Soul Power: How the 1960s Revolutionized American Music"'
                className="flex-1 min-w-[260px] bg-bg-surface border border-line rounded-md px-3 py-2 text-[12.5px] focus:outline-none focus:border-brand-amber" />
              <span className="flex items-center gap-1.5 text-[11px] text-ink-muted">
                <input type="number" min={0} max={5000} value={targetWords}
                  onChange={(e) => setTargetWords(e.target.value)}
                  data-testid="wd-target-words"
                  className="w-16 bg-bg-surface border border-line rounded-md px-2 py-2 text-right text-[12px]" />
                + words
              </span>
              <button onClick={() => runAi("generate")} disabled={!!aiBusy}
                data-testid="wd-ai-generate"
                className="inline-flex items-center gap-1.5 text-xs font-bold px-3 py-2 rounded-md bg-brand-amber text-black hover:bg-brand-amber/90 transition disabled:opacity-50">
                {aiBusy === "generate" ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Sparkles className="h-3.5 w-3.5" />} Generate
              </button>
              <button onClick={() => runAi("longer")} disabled={!!aiBusy}
                data-testid="wd-ai-longer"
                className="inline-flex items-center gap-1.5 text-xs px-3 py-2 rounded-md border border-line hover:border-brand-amber hover:text-brand-amber transition disabled:opacity-50">
                {aiBusy === "longer" ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : null} Make longer
              </button>
              <button onClick={() => runAi("rewrite")} disabled={!!aiBusy}
                data-testid="wd-ai-rewrite"
                className="inline-flex items-center gap-1.5 text-xs px-3 py-2 rounded-md border border-line hover:border-brand-amber hover:text-brand-amber transition disabled:opacity-50">
                {aiBusy === "rewrite" ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : null} Rewrite
              </button>
            </div>
          </div>

          {/* Title */}
          <input value={title}
            onChange={(e) => { setTitle(e.target.value); scheduleSave(); }}
            data-testid="wd-title-input"
            placeholder="Document title…"
            className="w-full bg-transparent text-xl font-display font-bold placeholder:text-ink-muted focus:outline-none mb-3" />

          {/* Toolbar */}
          <div className="flex items-center gap-1 border-b border-line pb-2 mb-3">
            {TOOLBAR.map((t) => (
              <button key={t.tid} onMouseDown={(e) => e.preventDefault()} onClick={t.cmd}
                title={t.label} data-testid={t.tid}
                className="w-8 h-8 rounded-md grid place-items-center text-ink-secondary hover:text-brand-amber hover:bg-bg-elevated transition">
                <t.icon className="h-4 w-4" />
              </button>
            ))}
            <div className="ml-auto flex items-center gap-2 text-[11px] text-ink-muted">
              <span data-testid="wd-word-count">{words} words</span>
              <span>·</span>
              <span data-testid="wd-save-status" className="inline-flex items-center gap-1">
                {saving ? (<><Loader2 className="h-3 w-3 animate-spin" /> Saving…</>)
                  : savedAt ? (<><Check className="h-3 w-3 text-emerald-400" /> Saved</>) : "—"}
              </span>
            </div>
          </div>

          {/* Editor surface */}
          <div
            ref={editorRef}
            contentEditable
            suppressContentEditableWarning
            onInput={onEditorInput}
            data-testid="wd-editor"
            className="wd-editor min-h-[320px] max-h-[55vh] overflow-y-auto text-[14px] leading-relaxed text-ink-primary focus:outline-none
                       [&_h2]:text-lg [&_h2]:font-bold [&_h2]:font-display [&_h2]:mt-4 [&_h2]:mb-2
                       [&_h3]:text-base [&_h3]:font-semibold [&_h3]:mt-3 [&_h3]:mb-1.5
                       [&_p]:mb-2.5 [&_ul]:list-disc [&_ul]:pl-5 [&_ul]:mb-2.5 [&_ol]:list-decimal [&_ol]:pl-5 [&_ol]:mb-2.5"
          />

          {/* Export actions */}
          <div className="flex flex-wrap items-center gap-2 border-t border-line pt-3 mt-3">
            <button onClick={copyText} data-testid="wd-copy-text"
              className="inline-flex items-center gap-1.5 text-xs px-3 py-2 rounded-md border border-line hover:border-brand-amber hover:text-brand-amber transition">
              <Copy className="h-3.5 w-3.5" /> Copy text
            </button>
            <button onClick={copyHtml} data-testid="wd-copy-html"
              className="inline-flex items-center gap-1.5 text-xs px-3 py-2 rounded-md border border-line hover:border-brand-amber hover:text-brand-amber transition">
              <Copy className="h-3.5 w-3.5" /> Copy HTML
            </button>
            <button onClick={downloadTxt} data-testid="wd-download-txt"
              className="inline-flex items-center gap-1.5 text-xs px-3 py-2 rounded-md border border-line hover:border-brand-amber hover:text-brand-amber transition">
              <FileDown className="h-3.5 w-3.5" /> Download .txt
            </button>
            <button onClick={openEmailDialog} data-testid="wd-email-doc"
              className="ml-auto inline-flex items-center gap-1.5 text-xs font-bold px-3.5 py-2 rounded-md bg-brand-amber/15 border border-brand-amber/40 text-brand-amber hover:bg-brand-amber/25 transition">
              <Mail className="h-3.5 w-3.5" /> Email to sponsor
            </button>
            <button onClick={sendToBlog} disabled={sending} data-testid="wd-send-to-blog"
              className="inline-flex items-center gap-1.5 text-xs font-bold px-3.5 py-2 rounded-md bg-brand-blue/15 border border-brand-blue/40 text-brand-blue hover:bg-brand-blue/25 transition disabled:opacity-50">
              {sending ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Send className="h-3.5 w-3.5" />}
              Send to Blog (WordPress draft)
            </button>
          </div>
        </div>
      </div>

      {/* ── Email-to-sponsor dialog ────────────────────── */}
      <Dialog open={emailOpen} onOpenChange={setEmailOpen}>
        <DialogContent className="sm:max-w-md" data-testid="wd-email-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Mail className="h-4 w-4 text-brand-amber" /> Email this document
            </DialogTitle>
            <DialogDescription>
              Sends the document as a polished email. Replies go straight to your inbox.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div>
              <label className="text-[11px] font-semibold text-ink-secondary block mb-1">Recipient email</label>
              <input value={emailTo} onChange={(e) => setEmailTo(e.target.value)}
                data-testid="wd-email-to" type="email"
                placeholder="sponsor@business.com"
                className="w-full bg-bg-surface border border-line rounded-md px-3 py-2 text-[13px] focus:outline-none focus:border-brand-amber" />
            </div>
            <div>
              <label className="text-[11px] font-semibold text-ink-secondary block mb-1">Subject</label>
              <input value={emailSubject} onChange={(e) => setEmailSubject(e.target.value)}
                data-testid="wd-email-subject"
                className="w-full bg-bg-surface border border-line rounded-md px-3 py-2 text-[13px] focus:outline-none focus:border-brand-amber" />
            </div>
            <div>
              <label className="text-[11px] font-semibold text-ink-secondary block mb-1">Personal note <span className="text-ink-muted font-normal">(optional — shown above the pitch)</span></label>
              <textarea value={emailNote} onChange={(e) => setEmailNote(e.target.value)}
                data-testid="wd-email-note" rows={2}
                placeholder="Hi Sam — great chatting at the market on Saturday. Here's what we discussed…"
                className="w-full bg-bg-surface border border-line rounded-md px-3 py-2 text-[13px] focus:outline-none focus:border-brand-amber resize-none" />
            </div>
          </div>
          <DialogFooter>
            <button onClick={() => setEmailOpen(false)} data-testid="wd-email-cancel"
              className="text-xs px-3 py-2 rounded-md border border-line hover:border-brand-amber hover:text-brand-amber transition">
              Cancel
            </button>
            <button onClick={sendDocEmail} disabled={emailSending || !emailTo.trim()}
              data-testid="wd-email-send"
              className="inline-flex items-center gap-1.5 text-xs font-bold px-4 py-2 rounded-md bg-brand-amber text-black hover:bg-brand-amber/90 transition disabled:opacity-50">
              {emailSending ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Send className="h-3.5 w-3.5" />}
              Send email
            </button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
